/**
 * Decision Memory tools — mine sessions for decisions, add/query/invalidate
 * decisions, search across the decision knowledge graph, and view timelines.
 */

import crypto from 'node:crypto';
import type { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import { optionalNonEmptyString } from './_zod-helpers.js';
import {
  DEFAULT_REJECT_THRESHOLD,
  DEFAULT_REVIEW_THRESHOLD,
  classifyConfidence,
  mineSessions,
} from '../../memory/conversation-miner.js';
import {
  computeConfidence,
  resetCachedWeights,
  setWeightTuningEnabled,
} from '../../memory/decision-confidence.js';
import {
  loadWeights,
  saveWeights,
  tuneConfidenceWeights,
  type TuneEvent,
} from '../../memory/confidence-tuner.js';
import { clusterDecisions, titleSimilarity } from '../../memory/decision-clusterer.js';
import { type ConsolidationVerdict, consolidateOne } from '../../memory/decision-consolidator.js';
import { minedDecisionRejectReason } from '../../memory/decision-quality.js';
import {
  EXPORT_LIMIT_DEFAULT,
  EXPORT_LIMIT_MAX,
  exportDecisionsAsJsonl,
  exportDecisionsAsMarkdown,
} from '../../memory/decision-exporter.js';
import type { ClusterRow, DecisionRow, DecisionType } from '../../memory/decision-store.js';
import { hybridRankDecisions } from '../../memory/decision-hybrid.js';
import { toDecisionIndexEntry } from '../../memory/decision-index.js';
import {
  type VerificationStatus,
  verifyDecision,
  verifyDecisions,
} from '../../memory/decision-verification.js';
import { generateProjectMemo } from '../../memory/project-memo.js';
import { indexSessions } from '../../memory/session-indexer.js';
import { isContentNonEnglish, sanitizeTitle } from '../../memory/title-extractor.js';
import { assembleWakeUp, assembleWakeUpSplit } from '../../memory/wake-up.js';
import { runRetriever } from '../../retrieval/index.js';
import { createSearchSessionsRetriever } from '../../retrieval/retrievers/search-sessions-retriever.js';
import type { ServerContext } from '../../server/types.js';
import { OutputFormatSchema, encodeResponse } from '../_common/output-format.js';
import { getCurrentBranch } from '../../utils/git-branch.js';
import { relativizeUnderRoot } from '../../utils/path-relativize.js';
import { withRecallTimeout } from '../../utils/recall-timeout.js';

const DECISION_TYPES = [
  'architecture_decision',
  'tech_choice',
  'bug_root_cause',
  'preference',
  'tradeoff',
  'discovery',
  'convention',
] as const;

// ─── remember_decision: per-session rate-limit + dedup state ─────────
//
// State lives at module scope so it persists across calls within the same
// daemon process. The limits are intentionally simple — a `Map` keyed by
// session id with a rolling 1h window. Entries older than 24h are pruned
// on each call so the map cannot grow unbounded.

interface RememberSessionState {
  /** Number of successful (non-dedup, non-rejected) writes inside the window. */
  count: number;
  /** Window start in unix ms. */
  firstAt: number;
  /** sha256(title|content|type|symbol_id|file_path) -> existing row id. */
  hashes: Map<string, number>;
  /** Last touch — used for the 24h pruning sweep. */
  lastTouchAt: number;
}

const REMEMBER_SESSION_LIMIT = 30; // 30 successful writes / session / hour
const REMEMBER_WINDOW_MS = 60 * 60 * 1000;
const REMEMBER_HASH_CAP = 200; // dedup hashes per session (LRU-ish)
const REMEMBER_SESSION_PRUNE_MS = 24 * 60 * 60 * 1000;

const rememberSessions = new Map<string, RememberSessionState>();

// ─── memo auto-regen: per-scope throttle state ──────────────────────
//
// Module-scope so the throttle survives across handler calls within the
// same daemon process. Keys are `projectRoot::serviceName` (serviceName
// empty when scope is project-wide). Values are the last time the
// fire-and-forget memo regen body was kicked off — NOT the time it
// completed. That's fine: if regen is in-flight when the next write
// lands, the throttle still suppresses a duplicate trigger.
const memoAutoTriggerState = new Map<string, number>();
/**
 * Auto-regen throttle entries older than this are pruned on the next
 * `maybeAutoRegenMemoAsync` call. The actual throttle window
 * (`minTriggerIntervalSec`) is much shorter — anything older than 7 days
 * is guaranteed stale and can be safely evicted.
 */
const MEMO_AUTO_TRIGGER_PRUNE_MS = 7 * 24 * 60 * 60 * 1000;

function memoScopeKey(projectRoot: string, serviceName?: string): string {
  return `${projectRoot}::${serviceName ?? ''}`;
}

/** Drop entries whose last trigger is older than `MEMO_AUTO_TRIGGER_PRUNE_MS`. */
function pruneMemoAutoTriggerState(now: number): void {
  for (const [key, lastTriggerMs] of memoAutoTriggerState.entries()) {
    if (now - lastTriggerMs > MEMO_AUTO_TRIGGER_PRUNE_MS) {
      memoAutoTriggerState.delete(key);
    }
  }
}

/** Test-only escape hatch — drives `pruneMemoAutoTriggerState` directly. */
export function _pruneMemoAutoTriggerStateForTests(now: number): void {
  pruneMemoAutoTriggerState(now);
}

/** Reset auto-regen throttle state — test-only escape hatch. */
export function _resetMemoAutoTriggerStateForTests(): void {
  memoAutoTriggerState.clear();
}

/**
 * Test-only escape hatch — inject a scope entry with a chosen timestamp so
 * tests can force-age the map without waiting for real time to pass.
 */
export function _setMemoAutoTriggerStateEntryForTests(
  projectRoot: string,
  serviceName: string | undefined,
  lastTriggerMs: number,
): void {
  memoAutoTriggerState.set(memoScopeKey(projectRoot, serviceName), lastTriggerMs);
}

/** Test-only inspector — returns the throttle map size. */
export function _memoAutoTriggerStateSizeForTests(): number {
  return memoAutoTriggerState.size;
}

/** Test-only inspector — true when an entry exists for the given scope. */
export function _memoAutoTriggerStateHasForTests(
  projectRoot: string,
  serviceName?: string,
): boolean {
  return memoAutoTriggerState.has(memoScopeKey(projectRoot, serviceName));
}

function pruneRememberSessions(now: number): void {
  for (const [key, state] of rememberSessions.entries()) {
    if (now - state.lastTouchAt > REMEMBER_SESSION_PRUNE_MS) {
      rememberSessions.delete(key);
    }
  }
}

function dedupHash(input: {
  title: string;
  content: string;
  type: string;
  symbol_id?: string;
  file_path?: string;
}): string {
  const h = crypto.createHash('sha256');
  h.update(input.title);
  h.update(' ');
  h.update(input.content);
  h.update(' ');
  h.update(input.type);
  h.update(' ');
  h.update(input.symbol_id ?? '');
  h.update(' ');
  h.update(input.file_path ?? '');
  return h.digest('hex');
}

/**
 * Shared runtime dependencies threaded into the module-level memo helpers.
 * Bundled so the helpers can live at module scope (keeping them out of the
 * `registerMemoryTools` closure and its cyclomatic budget) without a long
 * per-call parameter list.
 */
interface MemoHelperDeps {
  config: ServerContext['config'];
  aiProvider: ServerContext['aiProvider'];
  decisionStore: NonNullable<ServerContext['decisionStore']>;
}

/**
 * Build the LLM context required by memo/mining strategies that call the AI
 * provider. Returns null when AI is disabled or the inference service is the
 * no-op `FallbackInferenceService` — callers should fall back to regex.
 */
async function resolveLlmContextImpl(deps: MemoHelperDeps): Promise<{
  inference: ReturnType<NonNullable<ServerContext['aiProvider']>['inference']>;
  model: string;
} | null> {
  const { config, aiProvider } = deps;
  if (!aiProvider) return null;
  try {
    const available = await aiProvider.isAvailable();
    if (!available) return null;
  } catch {
    return null;
  }
  const inference = aiProvider.inference();
  // The active model — prefer the explicit config field; fall back to the
  // provider hint when none was set.
  const model =
    config?.ai?.inference_model ?? config?.ai?.fast_model ?? config?.ai?.provider ?? 'unknown';
  return { inference, model };
}

/**
 * Fire-and-forget memo auto-regen trigger. Called from the success path of
 * every write that lands a decision in the active store. Returns immediately —
 * the actual regen runs on a `setImmediate` so the write caller never blocks.
 * Failures inside the trigger are logged but never propagate back to the write.
 *
 * Guards (any one short-circuits):
 *   - `memory.memo.enabled === false`           — entire memo subsystem off
 *   - `memory.memo.autoRegenerate === false`    — feature disabled
 *   - No AI provider available                  — regen can't proceed
 *   - Throttle still in effect for this scope   — recent trigger
 *
 * The throttle is stamped BEFORE scheduling the async body so a write burst
 * arriving in the same tick cannot all win the race.
 */
function maybeAutoRegenMemoImpl(
  deps: MemoHelperDeps,
  projectRootArg: string,
  serviceName?: string,
): void {
  const { config, aiProvider, decisionStore } = deps;
  const memoCfg = config?.memory?.memo;
  if (!memoCfg) return;
  if (memoCfg.enabled === false) return;
  if (memoCfg.autoRegenerate === false) return;
  if (!aiProvider) return;
  const store = decisionStore;
  const minIntervalSec = memoCfg.minTriggerIntervalSec ?? 600;
  const everyN = memoCfg.regenerateEveryN ?? 50;
  const targetTokens = memoCfg.targetTokens ?? 350;
  const scopeKey = memoScopeKey(projectRootArg, serviceName);
  const now = Date.now();
  // Amortized housekeeping — cheap Map sweep on every call, bounded by
  // the number of distinct (project, service) scopes the daemon has
  // seen. No separate timer.
  pruneMemoAutoTriggerState(now);
  const lastTriggerMs = memoAutoTriggerState.get(scopeKey);
  if (lastTriggerMs !== undefined && now - lastTriggerMs < minIntervalSec * 1000) {
    return;
  }
  // Stamp BEFORE scheduling — a burst of writes arriving in the same
  // tick must not all win the race.
  memoAutoTriggerState.set(scopeKey, now);

  setImmediate(async () => {
    try {
      const sinceLast = store.countDecisionsSinceLastMemo({
        project_root: projectRootArg,
        service_name: serviceName,
      });
      if (sinceLast < everyN) return;

      const llm = await resolveLlmContextImpl(deps);
      if (!llm) return;

      const decisions = store.queryDecisions({
        project_root: projectRootArg,
        service_name: serviceName,
        limit: 200,
      });
      const clusters = store.listClusters({
        project_root: projectRootArg,
        service_name: serviceName,
        order_by: 'decision_count',
        limit: 8,
      });
      const projectName = projectRootArg.split('/').pop() || projectRootArg;

      const AUTO_MEMO_BUDGET_MS = 60_000;
      const result = await Promise.race([
        generateProjectMemo(
          {
            decisions,
            clusters,
            project_name: projectName,
            service_name: serviceName,
          },
          {
            provider: llm.inference,
            model: llm.model,
            targetTokens,
          },
        ),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('memo-timeout')), AUTO_MEMO_BUDGET_MS),
        ),
      ]);

      if (!result.memo_md) return;

      const lastDecisionId =
        decisions.length > 0
          ? decisions.reduce((max, d) => (d.id > max ? d.id : max), 0)
          : undefined;
      store.saveProjectMemo({
        project_root: projectRootArg,
        service_name: serviceName,
        memo_md: result.memo_md,
        model: llm.model,
        last_decision_id: lastDecisionId,
        decisions_at_generation: decisions.length,
        clusters_at_generation: clusters.length,
        estimated_tokens: result.estimated_tokens,
      });
    } catch (e) {
      try {
        // Lazy-import the logger so the test fake McpServer doesn't need
        // to provide one — the production logger is already in scope as a
        // module-level singleton.
        const { logger } = await import('../../logger.js');
        logger.warn(
          { scope: scopeKey, err: String((e as Error)?.message ?? e) },
          'auto-regen-memo failed',
        );
      } catch {
        /* swallow logger import failures defensively */
      }
    }
  });
}

export function registerMemoryTools(server: McpServer, ctx: ServerContext): void {
  const { projectRoot, j, decisionStore, topoStore, config, aiProvider } = ctx;
  if (!decisionStore) return;
  // Shared runtime deps for the module-level memo helpers. `decisionStore` is
  // NonNullable past the guard above.
  const memoDeps: MemoHelperDeps = { config, aiProvider, decisionStore };
  const recallTimeoutMs = config?.memory?.recall?.timeoutMs ?? 5000;
  const miningCfg = config?.memory?.mining;
  const defaultMiningStrategy = miningCfg?.strategy ?? 'regex';
  // P2.5 — honor `memory.weight_tuning.enabled = false` by forcing the legacy
  // fixed-weight scorer regardless of any persisted ~/.trace-mcp/confidence_weights.json.
  setWeightTuningEnabled(config?.memory?.weight_tuning?.enabled ?? true);
  // Heat / time-decay scoring (P2.1). When disabled the read paths still
  // work — recordHits writes are skipped and order_by='heat' degrades to
  // order_by='recency' inside the store.
  const heatCfg = config?.memory?.heat;
  const heatEnabled = heatCfg?.enabled !== false;
  const heatHalfLifeDays = heatCfg?.halfLifeDays;
  const heatFreshnessDays = heatCfg?.freshnessDays;

  /**
   * Fire-and-forget hit recorder. Pushed off the response path via
   * queueMicrotask so a slow UPDATE never blocks the read return. Wrapped
   * in try/catch — a hit-update failure must never fail the caller's read.
   */
  function recordHitsAsync(ids: number[]): void {
    if (!heatEnabled || ids.length === 0) return;
    if (!decisionStore) return;
    const store = decisionStore;
    queueMicrotask(() => {
      try {
        store.recordHits(ids);
      } catch (err) {
        // Best-effort accounting only; swallow.
        void err;
      }
    });
  }

  // Thin closure bindings over the module-level impls — keeps all existing
  // call sites unchanged while moving the branch weight out of this function.
  const maybeAutoRegenMemoAsync = (projectRootArg: string, serviceName?: string): void =>
    maybeAutoRegenMemoImpl(memoDeps, projectRootArg, serviceName);

  const resolveLlmContext = (): ReturnType<typeof resolveLlmContextImpl> =>
    resolveLlmContextImpl(memoDeps);

  /** Get subproject names within this project */
  function getSubprojectNames(): string[] {
    if (!topoStore) return [];
    try {
      const repos = topoStore.getSubprojectsByProject(projectRoot);
      return repos.map((r) => r.name);
    } catch {
      return [];
    }
  }

  // ── mine_sessions ─────────────────────────────────────────────────

  server.tool(
    'mine_sessions',
    'Mine Claude Code / Claw Code session logs for architectural decisions, tech choices, bug root causes, and preferences. Strategies: "regex" (default, free, ~20-40% recall), "llm" (higher recall, costs tokens), "hybrid" (regex + LLM safety net). Skips already-mined sessions unless force=true. Mutates the decision store; idempotent. Returns JSON: { mined, decisions_extracted, sessions_processed, strategy?, llm_sessions?, llm_decisions_extracted? }.',
    {
      project_root: z
        .string()
        .max(1024)
        .optional()
        .describe('Only mine sessions for this project path (default: all projects)'),
      force: z.boolean().optional().describe('Re-mine already processed sessions (default: false)'),
      min_confidence: z
        .number()
        .min(0)
        .max(1)
        .optional()
        .describe(
          'Legacy reject floor — drops decisions below this. Superseded by reject_threshold; kept for back-compat.',
        ),
      review_threshold: z
        .number()
        .min(0)
        .max(1)
        .optional()
        .describe(
          'Auto-approve cutoff (default: config decisions.review_threshold, fallback 0.75). Decisions ≥ this enter the active graph immediately.',
        ),
      reject_threshold: z
        .number()
        .min(0)
        .max(1)
        .optional()
        .describe(
          'Reject floor (default: config decisions.reject_threshold, fallback 0.45). Decisions in [reject_threshold, review_threshold) queue for review; below it, dropped.',
        ),
      strategy: z
        .enum(['regex', 'llm', 'hybrid'])
        .optional()
        .describe(
          'Extraction strategy: regex (default, free/fast/low recall), llm (AI provider, costs tokens, higher recall), hybrid (regex + LLM safety net). Falls back to regex with a warning if no AI provider is configured.',
        ),
      incremental_cursor: z
        .boolean()
        .optional()
        .describe(
          'Per-call override for `memory.mining.incrementalCursor`. true (default) reuses byte-offset cursors for appended turns; false falls back to legacy mined/unmined semantics.',
        ),
    },
    async ({
      project_root,
      force,
      min_confidence,
      review_threshold,
      reject_threshold,
      strategy,
      incremental_cursor,
    }) => {
      const requestedStrategy = strategy ?? defaultMiningStrategy;
      const llmContext = requestedStrategy === 'regex' ? null : await resolveLlmContext();
      // Per-call `incremental_cursor` wins; otherwise fall through to the
      // `memory.mining.incrementalCursor` config knob. `mineSessions` itself
      // defaults to true when both are undefined.
      const cursorPref = incremental_cursor ?? miningCfg?.incrementalCursor;
      const result = await mineSessions(decisionStore, {
        projectRoot: project_root ?? projectRoot,
        force,
        minConfidence: min_confidence,
        reviewThreshold: review_threshold ?? ctx.config?.decisions?.review_threshold,
        rejectThreshold: reject_threshold ?? ctx.config?.decisions?.reject_threshold,
        strategy: requestedStrategy,
        llmContext: llmContext ?? undefined,
        llmKnobs: {
          maxTokensPerSession: miningCfg?.llm?.maxTokensPerSession,
          minSessionLength: miningCfg?.llm?.minSessionLength,
          maxSessions: miningCfg?.llm?.maxSessions,
        },
        incrementalCursor: cursorPref,
      });
      if (result.decisions_extracted > 0) {
        maybeAutoRegenMemoAsync(project_root ?? projectRoot, undefined);
      }
      return { content: [{ type: 'text', text: j(result) }] };
    },
  );

  // ── add_decision ──────────────────────────────────────────────────

  server.tool(
    'add_decision',
    'Manually record an architectural decision, tech choice, preference, or convention. Links to code symbols/files and optionally to a specific subproject for code-aware memory. Decisions have temporal validity — they can be invalidated later when they become outdated. Mutates the decision store (creates a new record). For automated extraction from session logs use mine_sessions instead. Returns JSON: { added: { id, title, type } }.',
    {
      title: z.string().min(1).max(200).describe('Short summary of the decision'),
      content: z
        .string()
        .min(1)
        .max(5000)
        .describe('Full decision text — reasoning, context, tradeoffs'),
      type: z.enum(DECISION_TYPES).describe('Decision type'),
      service_name: z
        .string()
        .max(256)
        .optional()
        .describe('Subproject name this decision is about (e.g., "auth-api", "user-service")'),
      symbol_id: z
        .string()
        .max(512)
        .optional()
        .describe(
          'Symbol FQN this decision is about (e.g., "src/auth/provider.ts::AuthProvider#class")',
        ),
      file_path: optionalNonEmptyString(1024).describe('File path this decision is about'),
      tags: z
        .array(z.string().max(64))
        .max(20)
        .optional()
        .describe('Tags for categorization (e.g., ["auth", "security"])'),
      git_branch: z
        .string()
        .max(256)
        .nullable()
        .optional()
        .describe(
          'Git branch this decision belongs to. Omit to auto-detect; pass null for branch-agnostic (visible from every branch).',
        ),
    },
    async ({ title, content, type, service_name, symbol_id, file_path, tags, git_branch }) => {
      // Auto-capture from project root when caller omitted the field. `null`
      // is preserved as an explicit "branch-agnostic" signal.
      const resolvedBranch = git_branch === undefined ? getCurrentBranch(projectRoot) : git_branch;
      // Task 11 — auto-supersession. A new decision sharing the same state key
      // (project + type + code anchor) auto-invalidates the prior active one.
      // Conservative by construction: only fires on an exact anchor + type
      // match, so unrelated decisions are never touched.
      const { decision, superseded } = decisionStore.addDecisionWithSupersession({
        title,
        content,
        type: type as DecisionType,
        project_root: projectRoot,
        service_name,
        symbol_id,
        file_path,
        tags,
        source: 'manual',
        confidence: 1.0,
        git_branch: resolvedBranch,
      });
      maybeAutoRegenMemoAsync(projectRoot, service_name);
      const added: Record<string, unknown> = { added: decision };
      if (superseded.length > 0) added.superseded = superseded;
      return { content: [{ type: 'text', text: j(added) }] };
    },
  );

  // ── remember_decision ─────────────────────────────────────────────
  //
  // Live agent-write path. Thin wrapper around `addDecision` that scores
  // confidence, routes through the existing review queue, dedups within a
  // session, and rate-limits per session. NO schema change — uses the
  // existing `source` / `confidence` / `review_status` columns on
  // `DecisionInput`. The `source` is fixed to `'auto'` for now (see
  // plan-cognee-E07 §"What v1 got wrong").

  server.tool(
    'remember_decision',
    'Live agent write into the decision knowledge graph. Confidence-scores the input and routes it through the memoir review queue: high-confidence rows enter the active graph immediately, mid-confidence rows queue for human approval, low-confidence rows are dropped without persistence. Per-session dedup + rate-limit. Use during a session to capture decisions in real time. For manual high-confidence writes use add_decision; for post-hoc extraction from session logs use mine_sessions. Returns JSON: { id, review_status, confidence, deduplicated? }.',
    {
      title: z.string().min(1).max(200).describe('Short summary of the decision'),
      content: z
        .string()
        .min(1)
        .max(5000)
        .describe('Full decision text — reasoning, context, tradeoffs'),
      type: z.enum(DECISION_TYPES).describe('Decision type'),
      service_name: z
        .string()
        .max(256)
        .optional()
        .describe('Subproject name this decision is about (e.g., "auth-api", "user-service")'),
      symbol_id: z
        .string()
        .max(512)
        .optional()
        .describe(
          'Symbol FQN this decision is about (e.g., "src/auth/provider.ts::AuthProvider#class")',
        ),
      file_path: optionalNonEmptyString(1024).describe('File path this decision is about'),
      tags: z
        .array(z.string().max(64))
        .max(20)
        .optional()
        .describe('Tags for categorization (e.g., ["auth", "security"])'),
      session_id: z
        .string()
        .max(256)
        .optional()
        .describe('Session identifier for dedup/rate-limit (default: "_default")'),
      git_branch: z
        .string()
        .max(256)
        .nullable()
        .optional()
        .describe(
          'Git branch this decision belongs to. Omit to auto-detect, or pass null to make it branch-agnostic.',
        ),
    },
    async ({
      title,
      content,
      type,
      service_name,
      symbol_id,
      file_path,
      tags,
      session_id,
      git_branch,
    }) => {
      // English-only gate (project rule: "English only in code and content").
      // Sanitizer drops non-English titles, unbalanced fragments, and empties.
      // Content gets the same language filter so foreign prose can't leak
      // into the knowledge graph via the long-field side channel.
      const cleanTitle = sanitizeTitle(title);
      if (cleanTitle === null || isContentNonEnglish(content)) {
        return {
          content: [
            {
              type: 'text',
              text: j({
                id: null,
                review_status: 'rejected',
                confidence: 0,
                reason: 'non_english_or_unparseable',
              }),
            },
          ],
        };
      }
      // Use the sanitized form everywhere downstream — confidence scoring,
      // dedup hashing, and store insertion all reference `effectiveTitle`.
      const effectiveTitle = cleanTitle;
      const now = Date.now();
      pruneRememberSessions(now);

      const sid = session_id ?? '_default';
      let state = rememberSessions.get(sid);
      if (!state) {
        state = { count: 0, firstAt: now, hashes: new Map(), lastTouchAt: now };
        rememberSessions.set(sid, state);
      } else if (now - state.firstAt > REMEMBER_WINDOW_MS) {
        // Window rolled — reset counter (keep dedup hashes — content-equality
        // does not become valid again just because the hour ticked over).
        state.count = 0;
        state.firstAt = now;
      }
      state.lastTouchAt = now;

      // Dedup: same content+type within the session returns the prior row.
      const hash = dedupHash({ title: effectiveTitle, content, type, symbol_id, file_path });
      const existingId = state.hashes.get(hash);
      if (existingId !== undefined) {
        const existing = decisionStore.getDecision(existingId);
        return {
          content: [
            {
              type: 'text',
              text: j({
                id: existingId,
                review_status: existing?.review_status ?? null,
                confidence: existing?.confidence ?? 0,
                deduplicated: true,
              }),
            },
          ],
        };
      }

      // Rate-limit: cap successful writes inside the window.
      if (state.count >= REMEMBER_SESSION_LIMIT) {
        const retryAfterMs = Math.max(0, state.firstAt + REMEMBER_WINDOW_MS - now);
        return {
          content: [
            {
              type: 'text',
              text: j({
                error: 'rate_limited',
                message: `remember_decision: ${REMEMBER_SESSION_LIMIT} writes/hour per session reached`,
                retry_after_ms: retryAfterMs,
              }),
            },
          ],
          isError: true,
        };
      }

      const confidence = computeConfidence({
        title: effectiveTitle,
        content,
        type: type as DecisionType,
        symbol_id,
        file_path,
        tags,
        service_name,
      });

      const reviewThreshold = ctx.config?.decisions?.review_threshold ?? DEFAULT_REVIEW_THRESHOLD;
      const rejectThreshold = ctx.config?.decisions?.reject_threshold ?? DEFAULT_REJECT_THRESHOLD;
      const tier = classifyConfidence(confidence, reviewThreshold, rejectThreshold);

      if (tier === 'drop') {
        // Short-circuit BEFORE addDecision — do not write the row.
        return {
          content: [
            {
              type: 'text',
              text: j({ id: null, review_status: 'rejected', confidence }),
            },
          ],
        };
      }

      // 'auto' = visible by default (review_status NULL). 'pending' = queue.
      const reviewStatus: 'pending' | null = tier === 'pending' ? 'pending' : null;
      const resolvedBranch = git_branch === undefined ? getCurrentBranch(projectRoot) : git_branch;

      // Task 11 — auto-supersession, but ONLY for rows that land active
      // (`tier === 'auto'`). A pending/low-confidence guess must never
      // invalidate an approved decision; superseding waits for it to clear
      // review (an explicit approve does not re-run supersession — acceptable
      // for v1, kept conservative on purpose).
      const decisionInput = {
        title: effectiveTitle,
        content,
        type: type as DecisionType,
        project_root: projectRoot,
        service_name,
        symbol_id,
        file_path,
        tags,
        session_id: sid,
        source: 'auto' as const,
        confidence,
        review_status: reviewStatus,
        git_branch: resolvedBranch,
      };
      let supersededIds: number[] = [];
      let decision: DecisionRow;
      if (reviewStatus === null) {
        const res = decisionStore.addDecisionWithSupersession(decisionInput);
        decision = res.decision;
        supersededIds = res.superseded;
      } else {
        decision = decisionStore.addDecision(decisionInput);
      }

      // Record dedup + bump counter only on actual writes.
      state.count += 1;
      state.hashes.set(hash, decision.id);
      if (state.hashes.size > REMEMBER_HASH_CAP) {
        // Cheap LRU-ish prune: drop the oldest insertion.
        const firstKey = state.hashes.keys().next().value;
        if (firstKey !== undefined) state.hashes.delete(firstKey);
      }

      // Auto-regen trigger: only on writes that land in the active store
      // (tier === 'auto'). 'pending' rows wait for human review and are
      // not counted against the memo threshold by countDecisionsSinceLastMemo
      // semantics, so we skip them.
      if (tier === 'auto') {
        maybeAutoRegenMemoAsync(projectRoot, service_name);
      }

      return {
        content: [
          {
            type: 'text',
            text: j({
              id: decision.id,
              review_status: reviewStatus ?? 'approved',
              confidence,
              ...(supersededIds.length > 0 ? { superseded: supersededIds } : {}),
            }),
          },
        ],
      };
    },
  );

  // ── query_decisions ───────────────────────────────────────────────

  server.tool(
    'query_decisions',
    'Query the decision knowledge graph. Filter by type, subproject, code symbol, file path, tag, or time — answers "why was this architecture chosen?" with the actual decision record. Use service_name to scope to a subproject. Defaults to auto+human-approved decisions; use include_pending or review_status for other tiers. Rows carry `cluster_ids` when part of a topical cluster (see `clusters_summary`). Read-only. Returns JSON: { decisions: [{ id, title, type, content, tags, review_status, cluster_ids? }], clusters_summary?, total_results }. Supports `output_format: "toon"`. Capped by `memory.recall.timeoutMs` (default 5000ms); on timeout returns `{ decisions: [], total_results: 0, degraded: true }`.',
    {
      type: z.enum(DECISION_TYPES).optional().describe('Filter by decision type'),
      service_name: z
        .string()
        .max(256)
        .optional()
        .describe('Filter by subproject name (e.g., "auth-api")'),
      symbol_id: optionalNonEmptyString(512).describe('Filter by linked symbol FQN'),
      file_path: optionalNonEmptyString(1024).describe('Filter by linked file path'),
      tag: optionalNonEmptyString(64).describe('Filter by tag'),
      search: z
        .string()
        .max(500)
        .optional()
        .describe('Full-text search query (FTS5 with porter stemming)'),
      as_of: optionalNonEmptyString(30).describe('Only decisions active at this ISO timestamp'),
      include_invalidated: z
        .boolean()
        .optional()
        .describe('Include invalidated decisions (default: false)'),
      include_pending: z
        .boolean()
        .optional()
        .describe(
          'Also return decisions in the review queue (review_status="pending"). Default: false — only auto-approved and approved rows are returned.',
        ),
      review_status: z
        .enum(['pending', 'approved', 'rejected'])
        .optional()
        .describe(
          'Restrict to a single review tier (overrides default + include_pending). Use "pending" to fetch the review queue.',
        ),
      git_branch: z
        .string()
        .max(256)
        .optional()
        .describe(
          'Branch filter: "current" (default) = current branch + branch-agnostic; "all" = every branch; any other value = that branch + branch-agnostic.',
        ),
      order_by: z
        .enum(['recency', 'heat', 'created_at'])
        .optional()
        .describe(
          'Result ordering: "recency" (default, valid_from DESC), "created_at" DESC, or "heat" (time-decay favoring frequently-recalled + fresh; degrades to recency if disabled in config).',
        ),
      limit: z.number().int().min(1).max(100).optional().describe('Max results (default: 50)'),
      verify: z
        .boolean()
        .optional()
        .describe(
          'Staleness verification (default true). Checks each `symbol_id`-linked decision against the live index + git history; deleted/renamed/materially-changed code is flagged `verification` + `stale: true`. false skips the check.',
        ),
      verification: z
        .enum(['ok', 'symbol_missing', 'code_changed', 'stale'])
        .optional()
        .describe(
          'Filter by verification verdict (implies verify=true). "stale" = any flagged row; "ok" = verified-fresh only. Omit to return all rows annotated in place.',
        ),
      index_only: z
        .boolean()
        .optional()
        .describe(
          'Progressive disclosure (default false). true omits full `content` — just id, title, type, anchors, tags, ~1-line `summary`. Pick ids cheaply, then pull full content with `get_decision`.',
        ),
      output_format: OutputFormatSchema,
    },
    async ({
      type,
      service_name,
      symbol_id,
      file_path,
      tag,
      search,
      as_of,
      include_invalidated,
      include_pending,
      review_status,
      git_branch,
      order_by,
      limit,
      verify,
      verification,
      index_only,
      output_format,
    }) => {
      // Mirror addDecision's storage canonicalisation so a query with an
      // absolute path inside the project root still hits relative-stored rows.
      const queryFilePath = file_path
        ? (relativizeUnderRoot(file_path, projectRoot) ?? file_path)
        : file_path;
      // Resolve the three-mode branch filter:
      //   undefined/'current' → current branch + NULL
      //   'all'               → no filter
      //   <name>              → that branch + NULL
      const branchMode = git_branch ?? 'current';
      let branchFilter: string | null | 'all' | undefined;
      if (branchMode === 'all') {
        branchFilter = 'all';
      } else if (branchMode === 'current') {
        const current = getCurrentBranch(projectRoot);
        // When we can't resolve the branch (non-git/detached HEAD), fall back
        // to returning every row — the caller's "current" intent is satisfied
        // because there is no other branch context.
        branchFilter = current ?? 'all';
      } else {
        branchFilter = branchMode;
      }
      // When heat is disabled, gracefully treat order_by='heat' as 'recency'
      // so callers can pass the knob unconditionally.
      const effectiveOrderBy: 'recency' | 'heat' | 'created_at' =
        order_by === 'heat' && !heatEnabled ? 'recency' : (order_by ?? 'recency');
      // Task 9 — hybrid retrieval. When a free-text `search` is present and an
      // embedding service is configured, layer an embedding-similarity channel
      // on top of the FTS5 lexical channel and fuse with WRR (the same engine
      // as symbol `search`). FTS5 stays the zero-dependency fallback: no AI →
      // exactly the previous behaviour. We over-fetch a larger pool so the
      // rerank has candidates beyond the lexical top-N, then truncate.
      const hybridActive =
        !!search?.trim() && !!ctx.embeddingService && effectiveOrderBy === 'recency';
      const effectiveLimit = limit ?? 50;
      const poolLimit = hybridActive ? Math.min(Math.max(effectiveLimit * 3, 30), 200) : limit;
      const result = await withRecallTimeout<Record<string, unknown>>(
        async () => {
          const pool = decisionStore.queryDecisions({
            project_root: projectRoot,
            service_name,
            type: type as DecisionType | undefined,
            symbol_id,
            file_path: queryFilePath,
            tag,
            search,
            as_of,
            include_invalidated,
            include_pending,
            review_status,
            git_branch: branchFilter,
            order_by: effectiveOrderBy,
            heat_half_life_days: heatHalfLifeDays,
            heat_freshness_days: heatFreshnessDays,
            limit: poolLimit,
          });

          const decisions = hybridActive
            ? await hybridRankDecisions({
                query: search!,
                pool,
                embeddingService: ctx.embeddingService,
                reranker: ctx.reranker,
                limit: effectiveLimit,
                signal: undefined,
              })
            : pool;

          // Record recall hits for everything we are about to return so heat
          // scoring tracks real usage. Async + best-effort — never blocks.
          recordHitsAsync(decisions.map((d) => d.id));

          // P1.1 close-out — decorate each row with its cluster membership(s).
          // One JOIN over the membership table is cheaper than N per-row
          // lookups (and limit ≤ 100, so the IN-list cost is trivial).
          // Decisions in zero clusters get NO `cluster_ids` field — keeps
          // the typical (unclustered store) payload byte-identical.
          const decisionsWithClusters: Array<DecisionRow & { cluster_ids?: number[] }> = decisions;
          const clusterSummaryMap = new Map<number, { id: number; title: string }>();
          if (decisions.length > 0) {
            const ids = decisions.map((d) => d.id);
            const placeholders = ids.map(() => '?').join(',');
            type MemberRow = { decision_id: number; cluster_id: number };
            const memberRows = decisionStore.db
              .prepare(
                `SELECT decision_id, cluster_id
                   FROM decision_cluster_members
                  WHERE decision_id IN (${placeholders})`,
              )
              .all(...ids) as MemberRow[];
            if (memberRows.length > 0) {
              const byDecision = new Map<number, number[]>();
              const uniqueClusterIds = new Set<number>();
              for (const m of memberRows) {
                const arr = byDecision.get(m.decision_id) ?? [];
                arr.push(m.cluster_id);
                byDecision.set(m.decision_id, arr);
                uniqueClusterIds.add(m.cluster_id);
              }
              for (let i = 0; i < decisionsWithClusters.length; i++) {
                const row = decisionsWithClusters[i];
                const clusterIds = byDecision.get(row.id);
                if (clusterIds && clusterIds.length > 0) {
                  // Stable order — sort ASC so callers can diff payloads.
                  decisionsWithClusters[i] = {
                    ...row,
                    cluster_ids: [...clusterIds].sort((a, b) => a - b),
                  };
                }
              }
              // Title lookup per unique cluster id — cheap (1 PK fetch each),
              // bounded by the number of distinct clusters in the page.
              for (const cid of uniqueClusterIds) {
                const cluster = decisionStore.getCluster(cid);
                if (cluster) {
                  clusterSummaryMap.set(cid, { id: cluster.id, title: cluster.title });
                }
              }
            }
          }

          // Task 3 — staleness verification. On by default; an explicit
          // `verification` filter implies it. Decisions linked to a symbol_id
          // that was deleted/renamed (symbol_missing) or whose source changed
          // since created_at (code_changed) are flagged in place. The git work
          // only touches rows that actually carry a symbol_id, so the typical
          // unanchored store pays nothing.
          const verifyRequested = verify !== false || verification !== undefined;
          let verifiedRows: Array<
            DecisionRow & {
              cluster_ids?: number[];
              verification?: VerificationStatus;
              stale?: boolean;
            }
          > = decisionsWithClusters;
          if (verifyRequested) {
            verifiedRows = verifyDecisions(
              decisionsWithClusters,
              ctx.store,
              projectRoot,
            ) as typeof verifiedRows;
            if (verification) {
              verifiedRows = verifiedRows.filter((r) => {
                const v = r.verification ?? 'ok';
                if (verification === 'stale') return r.stale === true;
                if (verification === 'ok') return r.stale !== true;
                return v === verification;
              });
            }
          }

          // Task 12 — progressive disclosure. Project rows down to their index
          // entry (id + title + ~1-line summary, NO content) when requested.
          // Verification/cluster annotations are carried through.
          const outDecisions: unknown[] = index_only
            ? verifiedRows.map((r) => toDecisionIndexEntry(r))
            : verifiedRows;

          const stats = decisionStore.getStats(projectRoot);
          const serviceNames = getSubprojectNames();
          const payload: Record<string, unknown> = {
            decisions: outDecisions,
            total_results: outDecisions.length,
            store_stats: stats,
            ...(index_only ? { index_only: true } : {}),
          };
          if (clusterSummaryMap.size > 0) {
            payload.clusters_summary = [...clusterSummaryMap.values()].sort((a, b) => a.id - b.id);
          }
          if (serviceNames.length > 0) {
            payload.available_services = serviceNames;
          }
          return payload;
        },
        {
          timeoutMs: recallTimeoutMs,
          toolName: 'query_decisions',
          fallback: { decisions: [], total_results: 0, degraded: true },
        },
      );

      const fmt = output_format === 'markdown' ? 'json' : output_format;
      const text = encodeResponse(result, fmt);
      return { content: [{ type: 'text', text }] };
    },
  );

  // ── get_decision ──────────────────────────────────────────────────
  //
  // Progressive disclosure companion (Task 12): after picking an id from an
  // `index_only` query, pull the full decision — including `content` — by id.

  server.tool(
    'get_decision',
    'Fetch a single decision by id, including its full `content`. Companion to query_decisions `index_only: true` (progressive disclosure): list cheaply with index_only, then pull full content on demand for the ids you care about. Read-only. Returns JSON: { decision: { id, title, content, type, tags, ... } } or { error } when not found.',
    {
      id: z.number().int().min(1).describe('Decision id to fetch (from an index_only query).'),
      verify: z
        .boolean()
        .optional()
        .describe(
          'Staleness verification (default true). Adds `verification` + `stale` when the linked symbol was deleted/renamed or its source changed since `created_at`.',
        ),
    },
    async ({ id, verify }) => {
      const decision = decisionStore.getDecision(id);
      if (!decision) {
        return {
          content: [{ type: 'text', text: j({ error: `Decision ${id} not found` }) }],
          isError: true,
        };
      }
      // Count the fetch as a recall hit so heat scoring tracks real usage.
      recordHitsAsync([decision.id]);
      const out: Record<string, unknown> = { decision };
      if (verify !== false && decision.symbol_id) {
        const v = verifyDecision(decision, ctx.store, projectRoot);
        if (v.stale) {
          out.decision = { ...decision, verification: v.verification, stale: true };
        }
      }
      return { content: [{ type: 'text', text: j(out) }] };
    },
  );

  // ── export_decisions ──────────────────────────────────────────────

  server.tool(
    'export_decisions',
    'Export decisions to JSONL or Markdown. Read-only; no schema mutations. Use for audit, sharing with external tooling, or pre-LLM digestion. JSONL emits one decision per line with `tags` parsed from the on-disk JSON column into a real array. Markdown groups by type (and by service when multi-service). Hard-capped at 5000 rows per call as a cost guard. Returns JSON: { format, content, count, scope }.',
    {
      project_root: optionalNonEmptyString(1024).describe(
        'Project root to filter by (default: server projectRoot).',
      ),
      service_name: z
        .string()
        .max(256)
        .optional()
        .describe('Filter by subproject name (e.g., "auth-api").'),
      type: z.enum(DECISION_TYPES).optional().describe('Filter by decision type.'),
      git_branch: z
        .string()
        .max(256)
        .optional()
        .describe(
          'Branch filter. "all" → every branch (default); any other value → that branch + branch-agnostic rows.',
        ),
      include_invalidated: z
        .boolean()
        .optional()
        .describe('Include invalidated decisions (default: false).'),
      format: z
        .enum(['jsonl', 'markdown'])
        .default('jsonl')
        .describe('Output format (default: jsonl).'),
      limit: z
        .number()
        .int()
        .min(1)
        .max(EXPORT_LIMIT_MAX)
        .optional()
        .describe(
          `Max rows to export (default: ${EXPORT_LIMIT_DEFAULT}, hard max: ${EXPORT_LIMIT_MAX}).`,
        ),
    },
    async ({
      project_root,
      service_name,
      type,
      git_branch,
      include_invalidated,
      format,
      limit,
    }) => {
      const scopeProject = project_root ?? projectRoot;
      // Mirror query_decisions branch semantics: omitted means "all", any
      // explicit value means "that branch + branch-agnostic (NULL)" rows.
      // For export we default to "all" so the dump is comprehensive.
      const branchFilter: string | null | 'all' | undefined =
        git_branch === undefined || git_branch === 'all' ? 'all' : git_branch;
      // Apply the format default inside the handler so callers (and test
      // stubs) that bypass zod parsing still get the documented behaviour.
      const effectiveFormat: 'jsonl' | 'markdown' = format === 'markdown' ? 'markdown' : 'jsonl';
      const opts = {
        project_root: scopeProject,
        service_name,
        type: type as DecisionType | undefined,
        git_branch: branchFilter,
        include_invalidated,
        limit,
      };
      const { content, count } =
        effectiveFormat === 'markdown'
          ? exportDecisionsAsMarkdown(decisionStore, opts)
          : exportDecisionsAsJsonl(decisionStore, opts);

      return {
        content: [
          {
            type: 'text',
            text: j({
              format: effectiveFormat,
              content,
              count,
              scope: {
                project_root: scopeProject,
                service_name: service_name ?? null,
                type: type ?? null,
                git_branch: branchFilter,
                include_invalidated: include_invalidated ?? false,
                limit: limit ?? EXPORT_LIMIT_DEFAULT,
              },
            }),
          },
        ],
      };
    },
  );

  // ── invalidate_decision ───────────────────────────────────────────

  server.tool(
    'invalidate_decision',
    'Mark a decision as no longer valid. The decision remains in the knowledge graph for historical queries but is excluded from active queries. Use when a decision is superseded or reversed. Mutates the decision store; idempotent. Returns JSON: { invalidated: { id, title, valid_until } }.',
    {
      id: z.number().int().min(1).describe('Decision ID to invalidate'),
      valid_until: z
        .string()
        .max(30)
        .optional()
        .describe('ISO timestamp when decision became invalid (default: now)'),
    },
    async ({ id, valid_until }) => {
      const ok = decisionStore.invalidateDecision(id, valid_until);
      if (!ok) {
        return {
          content: [
            { type: 'text', text: j({ error: `Decision ${id} not found or already invalidated` }) },
          ],
          isError: true,
        };
      }
      const updated = decisionStore.getDecision(id);
      return { content: [{ type: 'text', text: j({ invalidated: updated }) }] };
    },
  );

  // ── approve_decision ──────────────────────────────────────────────

  server.tool(
    'approve_decision',
    'Approve a decision currently in the memoir-style review queue (review_status="pending"). Sets review_status="approved" so the row appears in default query_decisions results. Mutates the decision store; idempotent (re-approving an already-approved row is a no-op). Pair with reject_decision to clear out the review queue. Returns JSON: { approved: { id, title, review_status } }.',
    {
      id: z.number().int().min(1).describe('Decision ID to approve'),
    },
    async ({ id }) => {
      const ok = decisionStore.setReviewStatus(id, 'approved');
      if (!ok) {
        return {
          content: [{ type: 'text', text: j({ error: `Decision ${id} not found` }) }],
          isError: true,
        };
      }
      const updated = decisionStore.getDecision(id);
      // Transition to 'approved' lands the row in the active store — count
      // it against the auto-regen threshold.
      if (updated && updated.review_status === 'approved') {
        maybeAutoRegenMemoAsync(projectRoot, updated.service_name ?? undefined);
      }
      return { content: [{ type: 'text', text: j({ approved: updated }) }] };
    },
  );

  // ── reject_decision ───────────────────────────────────────────────

  server.tool(
    'reject_decision',
    'Reject a decision currently in the memoir-style review queue (review_status="pending"). Sets review_status="rejected" so the row is hidden from default query_decisions results but kept for audit. Mutates the decision store; idempotent. Use approve_decision for the opposite. Returns JSON: { rejected: { id, title, review_status } }.',
    {
      id: z.number().int().min(1).describe('Decision ID to reject'),
    },
    async ({ id }) => {
      const ok = decisionStore.setReviewStatus(id, 'rejected');
      if (!ok) {
        return {
          content: [{ type: 'text', text: j({ error: `Decision ${id} not found` }) }],
          isError: true,
        };
      }
      const updated = decisionStore.getDecision(id);
      return { content: [{ type: 'text', text: j({ rejected: updated }) }] };
    },
  );

  // ── tune_decision_weights ─────────────────────────────────────────

  server.tool(
    'tune_decision_weights',
    'Re-fit decision confidence weights from accumulated review feedback (approve/reject events). Requires >= min_events reviews and at least one of each label. Mutating: when dry_run=false and the fit succeeds, persists to ~/.trace-mcp/confidence_weights.json and resets the in-memory weight cache so subsequent remember_decision calls use the new weights. Returns: { ok, reason, events_used, weights?, before?, loss_before?, loss_after?, applied }.',
    {
      project_root: z
        .string()
        .max(1024)
        .optional()
        .describe('Filter review events to a specific project (default: current project root).'),
      min_events: z
        .number()
        .int()
        .min(10)
        .max(10000)
        .optional()
        .describe(
          'Minimum review events before tuning will fit (default: 25, configurable via memory.weight_tuning.min_events).',
        ),
      dry_run: z
        .boolean()
        .optional()
        .describe(
          'When true (default), compute weights without persisting to disk. Set false to apply the fit.',
        ),
    },
    async ({ project_root, min_events, dry_run }) => {
      const effectiveDryRun = dry_run ?? true;
      const minEventsCfg = config?.memory?.weight_tuning?.min_events ?? 25;
      const effectiveMinEvents = min_events ?? minEventsCfg;
      const effectiveProject = project_root ?? projectRoot;

      const rawEvents = decisionStore.listReviewEvents({ project_root: effectiveProject });
      const events: TuneEvent[] = rawEvents
        .filter(
          (r) =>
            // Drop rows with a malformed signal payload — listReviewEvents
            // zeroes them rather than throwing, but feeding empty-type rows
            // into the fitter would silently bias the perType slot.
            typeof r.signals?.type === 'string' && r.signals.type.length > 0,
        )
        .map((r) => ({
          signals: r.signals,
          label: r.action === 'approve' ? 1 : 0,
          confidence_at_decision: r.confidence_at_decision,
        }));

      const current = loadWeights();
      const result = tuneConfidenceWeights(events, current, {
        minEvents: effectiveMinEvents,
      });

      let applied = false;
      if (result.ok && !effectiveDryRun && result.weights) {
        saveWeights(result.weights);
        resetCachedWeights();
        applied = true;
      }

      return {
        content: [{ type: 'text', text: j({ ...result, applied, dry_run: effectiveDryRun }) }],
      };
    },
  );

  // ── get_decision_timeline ─────────────────────────────────────────

  server.tool(
    'get_decision_timeline',
    'Chronological timeline of decisions for a project, symbol, or file. Shows when decisions were made and invalidated — like git log but for architectural decisions. Read-only. Use to review decision history. Returns JSON: { timeline: [{ id, title, type, created_at, valid_until }], count }.',
    {
      symbol_id: z
        .string()
        .max(512)
        .optional()
        .describe('Filter timeline to decisions about this symbol'),
      file_path: z
        .string()
        .max(1024)
        .optional()
        .describe('Filter timeline to decisions about this file'),
      limit: z.number().int().min(1).max(200).optional().describe('Max entries (default: 100)'),
    },
    async ({ symbol_id, file_path, limit }) => {
      const timeline = decisionStore.getTimeline({
        project_root: projectRoot,
        symbol_id,
        file_path,
        limit,
      });
      // Timeline reads count as recall hits — same surface as query_decisions.
      recordHitsAsync(timeline.map((t) => t.id));
      return { content: [{ type: 'text', text: j({ timeline, count: timeline.length }) }] };
    },
  );

  // ── get_decision_stats ────────────────────────────────────────────

  server.tool(
    'get_decision_stats',
    'Overview of the decision knowledge graph: total decisions, active/invalidated counts, breakdown by type and source. Shows how much institutional knowledge is captured. Read-only. Returns JSON: { total, active, invalidated, by_type, by_source, sessions_mined }.',
    {},
    async () => {
      const stats = decisionStore.getStats(projectRoot);
      const minedCount = decisionStore.getMinedSessionCount();
      const chunkCount = decisionStore.getSessionChunkCount(projectRoot);
      const indexedSessions = decisionStore.getIndexedSessionIds(projectRoot);
      return {
        content: [
          {
            type: 'text',
            text: j({
              ...stats,
              sessions_mined: minedCount,
              sessions_indexed: indexedSessions.length,
              content_chunks: chunkCount,
            }),
          },
        ],
      };
    },
  );

  // ── build_decision_clusters (P1.1) ───────────────────────────────

  server.tool(
    'build_decision_clusters',
    'Recompute the L2 thematic cluster overlay over the decision store using the configured LLM. Stable cluster ids: a fresh cluster whose title matches an existing one (trigram Jaccard >=0.8) updates the existing row in place. Mutates the cluster store; idempotent. Requires an active AI provider — returns a structured error otherwise. Returns JSON: { created, updated, removed, total_after, clusters, strategy_used }.',
    {
      project_root: z
        .string()
        .max(1024)
        .optional()
        .describe('Project root to cluster (default: current project).'),
      service_name: z
        .string()
        .max(256)
        .optional()
        .describe('Filter input + scope of stored clusters to this subproject.'),
      max_clusters: z
        .number()
        .int()
        .min(1)
        .max(20)
        .optional()
        .describe('Hard cap on returned clusters (default: 8).'),
      force: z
        .boolean()
        .optional()
        .describe('Drop existing clusters in scope before rebuilding (default: false).'),
      dry_run: z
        .boolean()
        .optional()
        .describe('Compute clusters but do not write to the DB (default: false).'),
    },
    async ({ project_root, service_name, max_clusters, force, dry_run }) => {
      const scopeRoot = project_root ?? projectRoot;
      const llm = await resolveLlmContext();
      if (!llm) {
        return {
          content: [
            {
              type: 'text',
              text: j({
                error: 'no_ai_provider',
                message:
                  'build_decision_clusters requires an active AI provider. Configure ai.provider in your trace-mcp config and try again.',
                created: 0,
                updated: 0,
                removed: 0,
                total_after: decisionStore.countClusters({
                  project_root: scopeRoot,
                  service_name,
                }),
              }),
            },
          ],
          isError: true,
        };
      }

      // Fetch the input decision set. Active rows only — invalidated rows
      // would muddy topical clusters.
      const inputDecisions = decisionStore.queryDecisions({
        project_root: scopeRoot,
        service_name,
        limit: 500,
      });

      // 60-second budget for the whole clustering pass (LLM call(s) + writes).
      // Clustering is allowed to be slow; we just don't wait forever.
      const controller = new AbortController();
      const CLUSTER_BUDGET_MS = 60_000;
      let timedOut = false;
      const timer = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, CLUSTER_BUDGET_MS);
      timer.unref?.();

      let candidates: Awaited<ReturnType<typeof clusterDecisions>>;
      try {
        candidates = await Promise.race([
          clusterDecisions({
            decisions: inputDecisions,
            provider: llm.inference,
            model: llm.model,
            maxClusters: max_clusters,
            signal: controller.signal,
          }),
          new Promise<never>((_, reject) =>
            setTimeout(
              () => reject(new Error('build_decision_clusters: timeout')),
              CLUSTER_BUDGET_MS,
            ),
          ),
        ]);
      } catch (err) {
        clearTimeout(timer);
        return {
          content: [
            {
              type: 'text',
              text: j({
                error: timedOut ? 'timeout' : 'clustering_failed',
                message: (err as Error)?.message ?? String(err),
                created: 0,
                updated: 0,
                removed: 0,
                total_after: decisionStore.countClusters({
                  project_root: scopeRoot,
                  service_name,
                }),
              }),
            },
          ],
          isError: true,
        };
      }
      clearTimeout(timer);

      if (dry_run) {
        return {
          content: [
            {
              type: 'text',
              text: j({
                created: 0,
                updated: 0,
                removed: 0,
                total_after: decisionStore.countClusters({
                  project_root: scopeRoot,
                  service_name,
                }),
                clusters: candidates.map((c) => ({
                  title: c.title,
                  summary: c.summary,
                  decision_count: c.decision_ids.length,
                  primary_type: c.primary_type,
                  tags: c.tags,
                })),
                strategy_used: 'llm',
                dry_run: true,
              }),
            },
          ],
        };
      }

      // Apply: with force, wipe scope and create fresh rows. Without force,
      // merge by title similarity (>=0.8 trigram Jaccard) — same topic name
      // updates the existing row, otherwise insert new.
      let created = 0;
      let updated = 0;
      let removed = 0;
      if (force) {
        removed = decisionStore.deleteClustersForScope({
          project_root: scopeRoot,
          service_name,
        });
      }

      const existing = decisionStore.listClusters({
        project_root: scopeRoot,
        service_name,
        limit: 1000,
      });
      const SIM_THRESHOLD = 0.8;

      const finalRows: ClusterRow[] = [];
      for (const cand of candidates) {
        let match: ClusterRow | undefined;
        let matchSim = 0;
        for (const ex of existing) {
          const sim = titleSimilarity(cand.title, ex.title);
          if (sim >= SIM_THRESHOLD && sim > matchSim) {
            match = ex;
            matchSim = sim;
          }
        }
        if (match) {
          const updatedRow = decisionStore.updateCluster(match.id, {
            title: cand.title,
            summary: cand.summary,
            tags: cand.tags,
            primary_type: cand.primary_type ?? null,
            decision_ids: cand.decision_ids,
          });
          if (updatedRow) {
            updated++;
            finalRows.push(updatedRow);
          }
        } else {
          const newRow = decisionStore.createCluster({
            project_root: scopeRoot,
            service_name: service_name ?? null,
            title: cand.title,
            summary: cand.summary,
            tags: cand.tags,
            primary_type: cand.primary_type ?? null,
            decision_ids: cand.decision_ids,
          });
          created++;
          finalRows.push(newRow);
        }
      }

      return {
        content: [
          {
            type: 'text',
            text: j({
              created,
              updated,
              removed,
              total_after: decisionStore.countClusters({
                project_root: scopeRoot,
                service_name,
              }),
              clusters: finalRows.map((r) => ({
                id: r.id,
                title: r.title,
                decision_count: r.decision_count,
              })),
              strategy_used: 'llm',
            }),
          },
        ],
      };
    },
  );

  // ── consolidate_decisions (P2.2) ─────────────────────────────────

  server.tool(
    'consolidate_decisions',
    'LLM-driven semantic dedup of the decision store. For each decision in scope, finds top-K similar candidates (FTS + title-trigram) and asks the LLM to merge / replace / invalidate where appropriate. Mutating; respects dry_run (default true). Requires an active AI provider. Returns: { evaluated, verdicts: [{subject_id, verdict, affected_ids}], applied_count, dry_run }.',
    {
      project_root: z
        .string()
        .max(1024)
        .optional()
        .describe('Project root to consolidate (default: current project).'),
      service_name: z
        .string()
        .max(256)
        .optional()
        .describe('Filter input + scope of consolidation to this subproject.'),
      max_decisions: z
        .number()
        .int()
        .min(1)
        .max(500)
        .optional()
        .describe(
          'Cost guard: max subject decisions evaluated per call (default: config.memory.consolidation.defaultMaxDecisions or 50).',
        ),
      min_title_similarity: z
        .number()
        .min(0)
        .max(1)
        .optional()
        .describe(
          'Minimum trigram-title similarity for candidate consideration (default: config.memory.consolidation.defaultMinTitleSimilarity or 0.4).',
        ),
      same_type_only: z
        .boolean()
        .optional()
        .describe(
          'When true, only compare decisions of the same DecisionType (more conservative; default: config or false).',
        ),
      dry_run: z
        .boolean()
        .optional()
        .describe(
          'When true (default), compute verdicts without writing. Set false to apply merges / invalidations.',
        ),
      purge_low_quality: z
        .boolean()
        .optional()
        .describe(
          'Maintenance mode (no AI required). true invalidates active MINED/AUTO decisions failing the quality gate (truncated titles, broken-encoding summaries, non-English fragments). Respects dry_run (default true = preview). Manual decisions are never touched.',
        ),
    },
    async ({
      project_root,
      service_name,
      max_decisions,
      min_title_similarity,
      same_type_only,
      dry_run,
      purge_low_quality,
    }) => {
      const scopeRoot = project_root ?? projectRoot;
      const consolidationCfg = config?.memory?.consolidation;
      const effectiveDryRun = dry_run ?? true;

      // ── Maintenance mode: purge low-quality mined rows (no AI) ──────────
      // Runs the same quality gate used at extraction time against already-
      // stored MINED/AUTO decisions, and invalidates the failures. This is the
      // cleanup path for legacy garbage captured before the gate shipped.
      if (purge_low_quality) {
        const rows = decisionStore.queryDecisions({
          project_root: scopeRoot,
          service_name,
          limit: 10_000,
          rank_by_provenance: false,
        });
        const purged: Array<{ id: number; title: string; reason: string }> = [];
        for (const r of rows) {
          if (r.source === 'manual') continue; // never touch hand-authored rows
          const reason = minedDecisionRejectReason(r.title, r.content);
          if (reason === null) continue;
          if (!effectiveDryRun) {
            decisionStore.invalidateDecision(r.id);
          }
          purged.push({ id: r.id, title: r.title, reason });
        }
        return {
          content: [
            {
              type: 'text',
              text: j({
                mode: 'purge_low_quality',
                scanned: rows.length,
                purged_count: purged.length,
                purged,
                dry_run: effectiveDryRun,
              }),
            },
          ],
        };
      }

      const effectiveMaxDecisions = max_decisions ?? consolidationCfg?.defaultMaxDecisions ?? 50;
      const effectiveMinSim =
        min_title_similarity ?? consolidationCfg?.defaultMinTitleSimilarity ?? 0.4;
      const effectiveSameType = same_type_only ?? consolidationCfg?.defaultSameTypeOnly ?? false;

      const llm = await resolveLlmContext();
      if (!llm) {
        return {
          content: [
            {
              type: 'text',
              text: j({
                error: 'no_ai_provider',
                message:
                  'consolidate_decisions requires an active AI provider. Configure ai.provider in your trace-mcp config and try again.',
                evaluated: 0,
                verdicts: [],
                applied_count: 0,
                dry_run: effectiveDryRun,
              }),
            },
          ],
          isError: true,
        };
      }

      // Pull subjects newest-first; cost-bounded by `effectiveMaxDecisions`.
      const subjects = decisionStore.queryDecisions({
        project_root: scopeRoot,
        service_name,
        limit: effectiveMaxDecisions,
        order_by: 'recency',
      });

      // 120-second total budget — this is a bulk LLM op and is allowed
      // to be slow, but it must not pin the agent turn indefinitely.
      const CONSOLIDATE_BUDGET_MS = 120_000;
      const controller = new AbortController();
      let timedOut = false;
      const timer = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, CONSOLIDATE_BUDGET_MS);
      timer.unref?.();

      interface VerdictRecord {
        subject_id: number;
        verdict: ConsolidationVerdict;
        affected_ids: number[];
        applied: boolean;
      }
      const records: VerdictRecord[] = [];
      const invalidated = new Set<number>();
      const errors: Array<{ subject_id: number; message: string }> = [];
      let evaluated = 0;
      let applied_count = 0;

      try {
        for (const subject of subjects) {
          if (timedOut || controller.signal.aborted) break;
          // Skip subjects whose row was invalidated by an earlier verdict
          // in this same run — the LLM has nothing useful to say about
          // a tombstone.
          if (invalidated.has(subject.id)) continue;

          const candidates: DecisionRow[] = decisionStore
            .findSimilarDecisions({
              subject_id: subject.id,
              topK: 5,
              min_title_similarity: effectiveMinSim,
              same_type_only: effectiveSameType,
              project_root: scopeRoot,
              active_only: true,
            })
            // Defensive: drop any candidate this run already invalidated
            // so we never ask the LLM about a tombstone.
            .filter((c) => !invalidated.has(c.id));

          evaluated++;
          if (candidates.length === 0) continue;

          let verdicts: ConsolidationVerdict[];
          try {
            verdicts = await consolidateOne(
              { subject, candidates },
              {
                provider: llm.inference,
                model: llm.model,
                abortSignal: controller.signal,
              },
            );
          } catch (err) {
            errors.push({
              subject_id: subject.id,
              message: (err as Error)?.message ?? String(err),
            });
            continue;
          }

          for (const verdict of verdicts) {
            if (effectiveDryRun) {
              records.push({
                subject_id: subject.id,
                verdict,
                affected_ids: [],
                applied: false,
              });
              continue;
            }

            try {
              const result = decisionStore.applyConsolidationVerdict({
                subject_id: subject.id,
                verdict,
              });
              records.push({
                subject_id: subject.id,
                verdict,
                affected_ids: result.affected_ids,
                applied: result.applied,
              });
              if (result.applied) {
                applied_count++;
                for (const id of result.affected_ids) {
                  // Track invalidations so later iterations skip these rows.
                  // For merge_into_existing the subject is invalidated; for
                  // replace_existing / invalidate_existing the existing is.
                  // Either way the affected ids include the row whose
                  // validity window just closed.
                  if (verdict.kind === 'merge_into_existing' && id === subject.id) {
                    invalidated.add(id);
                  } else if (
                    (verdict.kind === 'replace_existing' ||
                      verdict.kind === 'invalidate_existing') &&
                    id === verdict.existing_id
                  ) {
                    invalidated.add(id);
                  }
                }
              }
            } catch (err) {
              errors.push({
                subject_id: subject.id,
                message: (err as Error)?.message ?? String(err),
              });
            }
          }
        }
      } finally {
        clearTimeout(timer);
      }

      return {
        content: [
          {
            type: 'text',
            text: j({
              evaluated,
              verdicts: records.map((r) => ({
                subject_id: r.subject_id,
                verdict: r.verdict,
                affected_ids: r.affected_ids,
                applied: r.applied,
              })),
              applied_count,
              dry_run: effectiveDryRun,
              timed_out: timedOut,
              errors: errors.length > 0 ? errors : undefined,
            }),
          },
        ],
      };
    },
  );

  // ── get_decision_clusters (P1.1) ─────────────────────────────────

  server.tool(
    'get_decision_clusters',
    'List decision clusters with optional full-text filter. Each row carries a short noun-phrase title, 1-3 sentence summary, member count, and a preview of member decision titles. Use to navigate the decision store by topic instead of chronologically. Read-only. Returns JSON: { clusters, total }.',
    {
      project_root: z
        .string()
        .max(1024)
        .optional()
        .describe('Filter by project root (default: current project).'),
      service_name: z.string().max(256).optional().describe('Filter by subproject name.'),
      search: z
        .string()
        .max(500)
        .optional()
        .describe('Full-text search across cluster title, summary, and tags (FTS5).'),
      limit: z
        .number()
        .int()
        .min(1)
        .max(100)
        .optional()
        .describe('Max clusters to return (default: 20).'),
      order_by: z
        .enum(['decision_count', 'updated_at', 'title'])
        .optional()
        .describe(
          'Sort order. decision_count (default) = largest first; updated_at = most-recently-rebuilt first; title = alphabetical.',
        ),
    },
    async ({ project_root, service_name, search, limit, order_by }) => {
      const scopeRoot = project_root ?? projectRoot;
      const result = await withRecallTimeout<Record<string, unknown>>(
        () => {
          const clusters = decisionStore.listClusters({
            project_root: scopeRoot,
            service_name,
            search,
            limit: limit ?? 20,
            order_by,
          });
          const enriched = clusters.map((c) => {
            let tags: string[] = [];
            if (c.tags) {
              try {
                const parsed = JSON.parse(c.tags);
                if (Array.isArray(parsed)) {
                  tags = parsed.filter((t): t is string => typeof t === 'string');
                }
              } catch {
                /* malformed tags JSON — ignore */
              }
            }
            const members = decisionStore.getClusterDecisions(c.id, { limit: 5 });
            return {
              id: c.id,
              title: c.title,
              summary: c.summary,
              tags,
              primary_type: c.primary_type,
              decision_count: c.decision_count,
              updated_at: c.updated_at,
              decisions_preview: members.map((m) => m.title),
            };
          });
          return { clusters: enriched, total: enriched.length };
        },
        {
          timeoutMs: recallTimeoutMs,
          toolName: 'get_decision_clusters',
          fallback: { clusters: [], total: 0, degraded: true },
        },
      );
      return { content: [{ type: 'text', text: j(result) }] };
    },
  );

  // ── get_cluster_decisions (P1.1) ─────────────────────────────────

  server.tool(
    'get_cluster_decisions',
    'Return the member decisions of a cluster, plus the cluster header. Use after get_decision_clusters to drill into a specific topic. Read-only. Returns JSON: { cluster, decisions }.',
    {
      id: z.number().int().min(1).describe('Cluster id (from get_decision_clusters).'),
      limit: z
        .number()
        .int()
        .min(1)
        .max(500)
        .optional()
        .describe('Max member decisions to return (default: 100).'),
      include_invalidated: z
        .boolean()
        .optional()
        .describe('Include invalidated decisions (default: false).'),
    },
    async ({ id, limit, include_invalidated }) => {
      const result = await withRecallTimeout<Record<string, unknown>>(
        () => {
          const cluster = decisionStore.getCluster(id);
          if (!cluster) return { cluster: null, decisions: [] };
          const decisions = decisionStore.getClusterDecisions(id, {
            limit,
            include_invalidated,
          });
          let tags: string[] = [];
          if (cluster.tags) {
            try {
              const parsed = JSON.parse(cluster.tags);
              if (Array.isArray(parsed)) {
                tags = parsed.filter((t): t is string => typeof t === 'string');
              }
            } catch {
              /* malformed tags JSON — ignore */
            }
          }
          return {
            cluster: {
              id: cluster.id,
              title: cluster.title,
              summary: cluster.summary,
              tags,
              primary_type: cluster.primary_type,
              decision_count: cluster.decision_count,
              updated_at: cluster.updated_at,
            },
            decisions,
          };
        },
        {
          timeoutMs: recallTimeoutMs,
          toolName: 'get_cluster_decisions',
          fallback: { cluster: null, decisions: [], degraded: true },
        },
      );
      return { content: [{ type: 'text', text: j(result) }] };
    },
  );

  // ── regenerate_project_memo (L3 orientation digest) ──────────────

  // Default auto-regen threshold (decisions added since last memo).
  const memoCfg = config?.memory?.memo;
  const memoEnabled = memoCfg?.enabled !== false;
  const memoRegenEveryN = memoCfg?.regenerateEveryN ?? 50;
  const memoTargetTokens = memoCfg?.targetTokens ?? 350;

  server.tool(
    'regenerate_project_memo',
    'Synthesise (or refresh) the project memo — a 250-400 word LLM-written orientation digest over the decision store. Skips work when fewer than `memory.memo.regenerateEveryN` decisions have been added since the last memo unless `force=true`. Requires an active AI provider — structured error otherwise.',
    {
      project_root: z
        .string()
        .max(1024)
        .optional()
        .describe('Project root to memo (default: current project).'),
      service_name: z
        .string()
        .max(256)
        .optional()
        .describe('Filter input + scope of stored memo to this subproject.'),
      force: z
        .boolean()
        .optional()
        .describe(
          'Regenerate unconditionally, ignoring the decisions-since-last-memo threshold (default: false).',
        ),
    },
    async ({ project_root, service_name, force }) => {
      const scopeRoot = project_root ?? projectRoot;
      const forceRegen = force === true;

      // Threshold gate — when force=false, skip regeneration if the store
      // hasn't moved enough since the last memo. Returns the existing memo
      // as-is so callers can still treat the response as the canonical memo.
      if (!forceRegen) {
        const sinceLast = decisionStore.countDecisionsSinceLastMemo({
          project_root: scopeRoot,
          service_name,
        });
        if (sinceLast < memoRegenEveryN) {
          const existing = decisionStore.getLatestProjectMemo({
            project_root: scopeRoot,
            service_name,
          });
          return {
            content: [
              {
                type: 'text',
                text: j({
                  regenerated: false,
                  reason: 'threshold_not_met',
                  decisions_since_last: sinceLast,
                  threshold: memoRegenEveryN,
                  memo: existing
                    ? {
                        id: existing.id,
                        version: existing.version,
                        memo_md: existing.memo_md,
                        estimated_tokens: existing.estimated_tokens,
                        updated_at: existing.updated_at,
                      }
                    : null,
                  decisions_at_generation: existing?.decisions_at_generation ?? 0,
                  clusters_at_generation: existing?.clusters_at_generation ?? 0,
                }),
              },
            ],
          };
        }
      }

      const llm = await resolveLlmContext();
      if (!llm) {
        return {
          content: [
            {
              type: 'text',
              text: j({
                error: 'no_ai_provider',
                message:
                  'regenerate_project_memo requires an active AI provider. Configure ai.provider in your trace-mcp config and try again.',
                regenerated: false,
                memo: null,
              }),
            },
          ],
          isError: true,
        };
      }

      // Build the input set: top-N active decisions + top clusters for scope.
      // Cap the decision pool — the generator caps per-section in-prompt, but
      // we don't want to drag the whole store into memory.
      const decisions = decisionStore.queryDecisions({
        project_root: scopeRoot,
        service_name,
        limit: 200,
      });
      const clusters = decisionStore.listClusters({
        project_root: scopeRoot,
        service_name,
        order_by: 'decision_count',
        limit: 8,
      });

      // 60-second wall-clock budget — clustering-style. Memo generation is
      // allowed to be slow, but it must not pin the agent turn forever.
      const controller = new AbortController();
      const MEMO_BUDGET_MS = 60_000;
      let timedOut = false;
      const timer = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, MEMO_BUDGET_MS);
      timer.unref?.();

      let result: Awaited<ReturnType<typeof generateProjectMemo>>;
      try {
        const projectName = scopeRoot.split('/').pop() || scopeRoot;
        result = await Promise.race([
          generateProjectMemo(
            {
              decisions,
              clusters,
              project_name: projectName,
              service_name,
            },
            {
              provider: llm.inference,
              model: llm.model,
              targetTokens: memoTargetTokens,
              abortSignal: controller.signal,
            },
          ),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('regenerate_project_memo: timeout')), MEMO_BUDGET_MS),
          ),
        ]);
      } catch (err) {
        clearTimeout(timer);
        return {
          content: [
            {
              type: 'text',
              text: j({
                error: timedOut ? 'timeout' : 'memo_generation_failed',
                message: (err as Error)?.message ?? String(err),
                regenerated: false,
                memo: null,
              }),
            },
          ],
          isError: true,
        };
      }
      clearTimeout(timer);

      if (!result.memo_md) {
        return {
          content: [
            {
              type: 'text',
              text: j({
                regenerated: false,
                reason: 'empty_or_boilerplate',
                memo: null,
                decisions_at_generation: decisions.length,
                clusters_at_generation: clusters.length,
              }),
            },
          ],
        };
      }

      // Compute drift inputs — highest decision id in scope, total counts.
      const lastDecisionId =
        decisions.length > 0
          ? decisions.reduce((max, d) => (d.id > max ? d.id : max), 0)
          : undefined;
      const saved = decisionStore.saveProjectMemo({
        project_root: scopeRoot,
        service_name,
        memo_md: result.memo_md,
        model: llm.model,
        last_decision_id: lastDecisionId,
        decisions_at_generation: decisions.length,
        clusters_at_generation: clusters.length,
        estimated_tokens: result.estimated_tokens,
      });

      const fresh = decisionStore.getLatestProjectMemo({
        project_root: scopeRoot,
        service_name,
      });
      return {
        content: [
          {
            type: 'text',
            text: j({
              regenerated: true,
              memo: fresh
                ? {
                    id: fresh.id,
                    version: fresh.version,
                    memo_md: fresh.memo_md,
                    estimated_tokens: fresh.estimated_tokens,
                    updated_at: fresh.updated_at,
                  }
                : {
                    id: saved.id,
                    version: saved.version,
                    memo_md: result.memo_md,
                    estimated_tokens: result.estimated_tokens,
                    updated_at: new Date().toISOString(),
                  },
              decisions_at_generation: decisions.length,
              clusters_at_generation: clusters.length,
            }),
          },
        ],
      };
    },
  );

  // ── get_project_memo (read) ──────────────────────────────────────

  server.tool(
    'get_project_memo',
    'Return the latest synthesised project memo for a scope. Optionally include up to `limit` prior versions as history. Read-only. Returns JSON: { memo, history? }.',
    {
      project_root: z
        .string()
        .max(1024)
        .optional()
        .describe('Project root (default: current project).'),
      service_name: z
        .string()
        .max(256)
        .optional()
        .describe('Subproject name to scope the memo to.'),
      include_history: z
        .boolean()
        .optional()
        .describe('Include prior memo versions in the response (default: false).'),
      limit: z
        .number()
        .int()
        .min(1)
        .max(50)
        .optional()
        .describe('Max history rows to return when include_history=true (default: 10).'),
    },
    async ({ project_root, service_name, include_history, limit }) => {
      const scopeRoot = project_root ?? projectRoot;
      const result = await withRecallTimeout<Record<string, unknown>>(
        () => {
          const memo = decisionStore.getLatestProjectMemo({
            project_root: scopeRoot,
            service_name,
          });
          const payload: Record<string, unknown> = { memo: memo ?? null };
          if (include_history) {
            payload.history = decisionStore.listProjectMemos({
              project_root: scopeRoot,
              service_name,
              limit: limit ?? 10,
            });
          }
          return payload;
        },
        {
          timeoutMs: recallTimeoutMs,
          toolName: 'get_project_memo',
          fallback: { memo: null, degraded: true },
        },
      );
      return { content: [{ type: 'text', text: j(result) }] };
    },
  );

  // memoEnabled is read here so the linter sees the use even when no
  // gating happens at the tool level (wake-up integration gates instead).
  void memoEnabled;

  // ── index_sessions ────────────────────────────────────────────────

  server.tool(
    'index_sessions',
    'Index conversation content from Claude Code / Claw Code sessions for cross-session search. Stores chunked messages in FTS5 — enables "what did we discuss about X?" queries across all past sessions. Skips already-indexed sessions unless force=true. Mutates the session index; idempotent. Use before search_sessions. Returns JSON: { indexed, sessions_processed, chunks_stored }.',
    {
      project_root: z
        .string()
        .max(1024)
        .optional()
        .describe('Only index sessions for this project path (default: current project)'),
      force: z
        .boolean()
        .optional()
        .describe('Re-index already processed sessions (default: false)'),
    },
    async ({ project_root, force }) => {
      const result = indexSessions(decisionStore, {
        projectRoot: project_root ?? projectRoot,
        force,
      });
      return { content: [{ type: 'text', text: j(result) }] };
    },
  );

  // ── search_sessions ───────────────────────────────────────────────

  server.tool(
    'search_sessions',
    'Search across all past session conversations. Finds what was discussed, decided, or debugged in previous sessions. Full-text search with porter stemming — e.g., "why did we switch to GraphQL", "auth middleware bug", "database migration approach". Requires index_sessions to be run first. Read-only. Returns JSON: { results: [{ session_id, text, score }], total_results }.',
    {
      query: z.string().min(1).max(500).describe('Search query (FTS5 with porter stemming)'),
      limit: z.number().int().min(1).max(50).optional().describe('Max results (default: 20)'),
    },
    async ({ query, limit }) => {
      const chunkCount = decisionStore.getSessionChunkCount(projectRoot);
      if (chunkCount === 0) {
        return {
          content: [
            {
              type: 'text',
              text: j({
                message:
                  'No sessions indexed yet. Run index_sessions first to enable cross-session search.',
                hint: 'Call index_sessions to index past conversation content.',
              }),
            },
          ],
        };
      }
      const retriever = createSearchSessionsRetriever({ store: decisionStore });
      const [results] = await runRetriever(retriever, {
        query,
        limit,
        projectRoot,
      });
      return {
        content: [
          {
            type: 'text',
            text: j({
              results,
              total_results: results.length,
              sessions_indexed: decisionStore.getIndexedSessionIds(projectRoot).length,
            }),
          },
        ],
      };
    },
  );

  // ── get_wake_up ───────────────────────────────────────────────────

  server.tool(
    'get_wake_up',
    'Compact orientation context (~300 tokens) for session start. By default returns a {stable, dynamic} split: stable content (project identity, conventions, architecture) is provider-cacheable when injected into system_prompt; dynamic content (recent activity) goes into the user message to avoid busting the system-prompt cache. Pass cache_split: false for the legacy flat shape. Hard-capped by `memory.recall.timeoutMs` (default 5000 ms); on timeout returns a degraded empty payload with `degraded: true` so the agent turn never blocks on slow IO.',
    {
      max_decisions: z
        .number()
        .int()
        .min(1)
        .max(30)
        .optional()
        .describe(
          'Max recent decisions to include (default: 10 for flat shape; 5 for split shape recent_decisions section)',
        ),
      auto_mine: z
        .boolean()
        .optional()
        .describe('Auto-mine sessions if decision store is empty (default: true)'),
      cache_split: z
        .boolean()
        .optional()
        .describe(
          'Return the {stable, dynamic} split shape for prompt-cache friendliness (default: true). Pass false for the legacy flat WakeUpContext shape.',
        ),
      service_name: optionalNonEmptyString(256).describe(
        'Subproject name to scope topics + memo lookups to. Project-wide when omitted.',
      ),
    },
    async ({ max_decisions, auto_mine, cache_split, service_name }) => {
      const useSplit = cache_split !== false;

      // Degraded fallback used when the recall budget is blown. Mirrors the
      // chosen shape (split vs flat) but with empty content + degraded:true so
      // downstream consumers can detect and surface it.
      const fallback: Record<string, unknown> = useSplit
        ? {
            stable: {
              project: { name: '', root: projectRoot },
              conventions: [],
              architecture: [],
              stats: {
                total_active: 0,
                total_decisions: 0,
                sessions_mined: 0,
                sessions_indexed: 0,
                by_type: {},
              },
            },
            dynamic: { recent_decisions: [], in_progress: [] },
            estimated_tokens: 0,
            degraded: true,
          }
        : {
            project: { name: '', root: projectRoot },
            decisions: { total_active: 0, recent: [] },
            memory: {
              total_decisions: 0,
              sessions_mined: 0,
              sessions_indexed: 0,
              by_type: {},
            },
            estimated_tokens: 0,
            degraded: true,
          };

      const payload = await withRecallTimeout<Record<string, unknown>>(
        async () => {
          // Auto-mine on first wake-up if store is empty
          const shouldAutoMine = auto_mine !== false;
          const stats = decisionStore.getStats(projectRoot);
          let mineResult:
            | (Awaited<ReturnType<typeof mineSessions>> & ReturnType<typeof indexSessions>)
            | undefined;
          if (shouldAutoMine && stats.total === 0) {
            const mined = await mineSessions(decisionStore, { projectRoot });
            // Also index session content for search
            const indexResult = indexSessions(decisionStore, { projectRoot });
            mineResult = { ...mined, ...indexResult };
          }

          const inner: Record<string, unknown> = useSplit
            ? {
                ...assembleWakeUpSplit(decisionStore, projectRoot, {
                  maxRecent: max_decisions,
                  heatEnabled,
                  heatHalfLifeDays,
                  heatFreshnessDays,
                  memoEnabled,
                  memoMaxBudgetTokens: memoCfg?.maxBudgetTokens,
                  store: ctx.store,
                  ...(service_name ? { service_name } : {}),
                }),
              }
            : {
                ...assembleWakeUp(decisionStore, projectRoot, {
                  maxDecisions: max_decisions,
                  store: ctx.store,
                }),
              };
          if (mineResult) {
            inner.auto_mined = mineResult;
          }
          // Record hits for the dynamic surfaces (recent_decisions + in_progress
          // in the split shape; the flat shape's `decisions.recent` is the
          // equivalent surface). Stable conventions/architecture are NOT
          // counted because they're always-shown — hits there are noise.
          if (useSplit) {
            const dyn = (inner.dynamic ?? {}) as {
              recent_decisions?: Array<{ id: number }>;
              in_progress?: Array<{ id: number }>;
            };
            const ids: number[] = [];
            for (const r of dyn.recent_decisions ?? []) ids.push(r.id);
            for (const r of dyn.in_progress ?? []) ids.push(r.id);
            recordHitsAsync(ids);
          } else {
            const flat = (inner.decisions ?? {}) as {
              recent?: Array<{ id: number }>;
            };
            recordHitsAsync((flat.recent ?? []).map((r) => r.id));
          }
          return inner;
        },
        {
          timeoutMs: recallTimeoutMs,
          toolName: 'get_wake_up',
          fallback,
        },
      );

      return { content: [{ type: 'text', text: j(payload) }] };
    },
  );
}
